import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, Outlet, redirect, useLocation } from '@tanstack/react-router';
import { ReauthProvider } from '../components/auth/reauth';
import { HintsProvider, serverHintStore } from '../components/guidance/hints';
import { ShortcutsDialog } from '../components/guidance/shortcuts';
import { TourProvider } from '../components/guidance/tour';
import { useChecklist } from '../components/help/checklist';
import { CommandPaletteProvider } from '../components/help/command-palette';
import { HelpPanelProvider } from '../components/help/help-panel';
import { AppShell } from '../components/layout/app-shell';
import { ConfirmProvider } from '../components/ui/confirm';
import { TooltipProvider } from '../components/ui/tooltip';
import { ActivityProvider } from '../lib/activity';
import { ColorModeProvider } from '../lib/colors';
import { isPublicPath, loadSession } from '../lib/session';

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // Everything except sign-in and access requests needs a full (authenticator-verified) session.
  beforeLoad: async ({ context, location }) => {
    if (isPublicPath(location.pathname)) return;
    const session = await loadSession(context.queryClient);
    if (session.status !== 'signed-in') throw redirect({ to: '/login', search: { next: location.href } });
  },
  component: Root,
});

function Root() {
  const { pathname } = useLocation();
  if (isPublicPath(pathname)) return <Outlet />;
  return <SignedIn />;
}

function SignedIn() {
  const checklist = useChecklist();
  return (
    <HintsProvider store={serverHintStore}>
      <TourProvider onFinish={id => id === 'home' && checklist.complete('tour')}>
        <TooltipProvider delayDuration={300}>
          <HelpPanelProvider>
            {/* Color mode wraps the palette: ⌘K results use the active "Color by" mode like every list. */}
            <ColorModeProvider>
              <CommandPaletteProvider>
                <ConfirmProvider>
                  <ActivityProvider>
                    <ReauthProvider>
                      <AppShell>
                        <Outlet />
                      </AppShell>
                    </ReauthProvider>
                    <ShortcutsDialog />
                  </ActivityProvider>
                </ConfirmProvider>
              </CommandPaletteProvider>
            </ColorModeProvider>
          </HelpPanelProvider>
        </TooltipProvider>
      </TourProvider>
    </HintsProvider>
  );
}
